<template>
  <Breadcrumb
    :items="breadcrumbItems"
    v-if="!acaoId"
  />

  <div
    class="header-menu-vagas"
    v-if="!acaoId"
  >
    <h2 class="titulo-pagina">Vagas de Extensão</h2>
    <div class="header-actions">
      <FiltrosComponent
        :isCoordenador="props.isCoordenador"
        :nomeTela="'vaga'"
        @aplicarFiltros="aplicarFiltros"
        @pesquisar="realizarPesquisa"
      />
    </div>
  </div>

  <FiltrosComponent
    v-else
    :isCoordenador="props.isCoordenador"
    :nomeTela="'vaga'"
    @aplicarFiltros="aplicarFiltros"
    @pesquisar="realizarPesquisa"
  />

  <Carregamento v-if="carregando || isLoading" />

  <section
    id="lista-vagas"
    v-else
  >
    <AvisoListagem v-if="vagas.length === 0">
      Não há vagas disponíveis no momento.
    </AvisoListagem>
    <template v-else>
      <div class="vagas-grid">
        <VagasCard
          v-for="(vaga, index) in vagas"
          :key="index"
          :vaga="vaga"
          :pode-editar="podeEditarOuExcluir(vaga)"
          @abrir="abrirVaga"
          @editar="editarVaga"
        />
      </div>
      <div class="paginacao-wrapper">
        <PaginacaoComponent
          :totalPaginas="totalPaginas"
          @mudar-pagina="togglePagina"
        />
      </div>
    </template>
  </section>

  <!-- Modal para Usuário Não Logado -->
  <div
    v-if="mostrarModalLogin"
    class="modal-overlay"
    @click.self="mostrarModalLogin = false"
  >
    <div class="modal-login-card">
      <div class="modal-login-header">
        <div class="modal-icon-wrapper">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
            <polyline points="10 17 15 12 10 7"></polyline>
            <line
              x1="15"
              y1="12"
              x2="3"
              y2="12"
            ></line>
          </svg>
        </div>
        <h3>Atenção</h3>
      </div>
      <div class="modal-login-body">
        <p>
          Para entrar em contato com os coordenadores desta ação, por favor faça
          login.
        </p>
      </div>
      <div class="modal-login-footer">
        <button
          class="btn-modal-ok"
          @click="mostrarModalLogin = false"
        >
          OK
        </button>
        <button
          class="btn-modal-entrar"
          @click="irParaLogin"
        >
          Entrar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import FiltrosComponent from "@/components/common/FiltrosComponent.vue";
import { formatarData, limitarTexto } from "@/utils/FormatarEntradas.js";
import Breadcrumb from "@/components/common/Breadcrumb.vue";
import { useRoute } from "vue-router";
import { useVagas } from "@/composables/useVagas.js";
import acaoService from "@/service/acaoService.js";
import router from "@/router/index.js";
import PaginacaoComponent from "@/components/common/paginacaoComponent.vue";
import Carregamento from "@/components/common/Carregamento.vue";
import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";
import userInfos from "@/utils/userInfos.js";
import dayjs from "dayjs";
import AvisoListagem from "../common/AvisoListagem.vue";
import VagasCard from "./VagasCard.vue";

const mostrarModalLogin = ref(false);

function irParaLogin() {
  router.push("/login");
}

const props = defineProps({
  isCoordenador: Boolean,
});

const { fetchVagasFiltradas, isLoading, totalPages } = useVagas();

const carregando = ref(false);

const route = useRoute();
const acaoId = computed(() => props.acaoId || route.params.id);
const vagas = ref([]);

const totalPaginas = ref(1);

const filtrosAtivos = ref({});

const authStore = useAuthStore();
const { isAutenticado, estaRenovando } = storeToRefs(authStore);
const isLogado = computed(() => isAutenticado.value || estaRenovando.value);

// Cache para evitar chamadas repetidas ao verificar curricularizada por acaoId
const cacheCurricularizada = {};

const breadcrumbItems = computed(() => {
  if (acaoId.value) {
    return [
      { label: "Início", to: "/" },
      { label: "Ações", to: "/suas-acoes" },
      { tipo: "acao", id: acaoId.value, to: `/acao/${acaoId.value}` },
      { label: "Vagas", to: `/acao/${acaoId.value}/vagas-acao` },
    ];
  } else {
    return [
      { label: "Início", to: "/" },
      { label: "Vagas", to: "/vagas" },
    ];
  }
});

function aplicarFiltros(filtros) {
  filtrosAtivos.value = { ...filtros };
}

function realizarPesquisa(termoBusca) {
  filtrosAtivos.value.titulo = termoBusca;
}

function abrirVaga(vaga) {
  if (acaoId.value) {
    router.push({
      name: "VagasViewAcao",
      params: { id: acaoId.value, vagaId: vaga.id },
    });
  } else {
    router.push({ name: "VagasView", params: { vagaId: vaga.id } });
  }
}

function editarVaga(vagaItem) {
  const targetAcaoId = acaoId.value || vagaItem.acaoId || vagaItem.acao?.id;
  if (targetAcaoId) {
    router.push({
      name: "editar-vaga",
      params: { id: targetAcaoId, vagaId: vagaItem.id },
    });
  } else {
    router.push(`/editar-vaga/${vagaItem.id}`);
  }
}

function togglePagina(pagina) {
  const query = { ...route.query };
  query.page = pagina;

  router.push({
    query: query,
  });
}

async function verificarCurricularizada(vagasList) {
  // Coletar acaoIds únicos que ainda não estão no cache
  const acaoIdsUnicos = [
    ...new Set(
      vagasList
        .map((v) => v.acaoId)
        .filter((id) => id && cacheCurricularizada[id] === undefined)
    ),
  ];

  // Buscar informações de cada ação em paralelo
  await Promise.all(
    acaoIdsUnicos.map(async (id) => {
      try {
        const acao = await acaoService.getAcoesById(id);
        cacheCurricularizada[id] = Boolean(
          acao?.curricularizada ?? acao?.data?.curricularizada ?? false
        );
      } catch {
        cacheCurricularizada[id] = false;
      }
    })
  );

  // Enriquecer cada vaga com o campo curricularizada
  return vagasList.map((v) => ({
    ...v,
    curricularizada: v.acaoId
      ? (cacheCurricularizada[v.acaoId] ?? false)
      : false,
  }));
}

function obterStatusEfetivo(vagaItem) {
  if (!vagaItem) return "ABERTA";

  if (vagaItem.status === "CANCELADA") return "CANCELADA";
  if (vagaItem.status === "ENCERRADA") return "ENCERRADA";

  const extrairApenasData = (dataVal) => {
    if (!dataVal) return null;
    if (Array.isArray(dataVal))
      return `${dataVal[0]}-${String(dataVal[1]).padStart(2, "0")}-${String(dataVal[2]).padStart(2, "0")}`;
    if (typeof dataVal === "string") return dataVal.split("T")[0];
    return dataVal;
  };

  const hoje = dayjs().startOf("day");

  if (vagaItem.dataFimInscricao) {
    const fimStr = extrairApenasData(vagaItem.dataFimInscricao);
    if (fimStr) {
      const fim = dayjs(fimStr).startOf("day");
      if (hoje.isAfter(fim)) {
        return "ENCERRADA";
      }
    }
  }

  if (vagaItem.dataInicioInscricao) {
    const inicioStr = extrairApenasData(vagaItem.dataInicioInscricao);
    if (inicioStr) {
      const inicio = dayjs(inicioStr).startOf("day");
      if (hoje.isBefore(inicio)) {
        return "BREVE";
      }
    }
  }

  return vagaItem.status || "ABERTA";
}

function obterClasseStatus(status) {
  if (status === "ABERTA") return "status-aberta";
  if (status === "BREVE") return "status-breve";
  if (status === "CANCELADA") return "status-cancelada";
  return "status-encerrada";
}

function formatarStatus(status) {
  if (status === "ABERTA") return "Inscrições Abertas";
  if (status === "BREVE") return "Inscrições em Breve";
  if (status === "CANCELADA") return "Vaga Cancelada";
  if (status === "ENCERRADA") return "Inscrições Encerradas";
  return status || "Inscrições Abertas";
}

function formatarTipoVaga(vagaItem) {
  if (vagaItem.isEstagiario) return "Estágio";
  if (vagaItem.isBolsistaRemunerado) return "Remunerada";
  return "Agente";
}

function obterClasseTipoVaga(vagaItem) {
  if (vagaItem.isEstagiario) return "tipo-estagio";
  if (vagaItem.isBolsistaRemunerado) return "tipo-remunerada";
  return "tipo-voluntaria";
}

function podeEditarOuExcluir(vagaItem) {
  if (!isLogado.value) return false;
  const user = userInfos.getUserInfos();
  if (!user) return false;

  const userId = user.id || user.userId || user.sub;
  const isAutor = Boolean(
    vagaItem.autorPublicacaoId && vagaItem.autorPublicacaoId === userId
  );
  const isAdmin =
    authStore.hasRole("ADMIN") ||
    authStore.hasRole("PREX_ADMIN") ||
    authStore.hasRole("ADMINISTRADOR");

  // Coordenador só pode editar se estiver dentro da sua própria ação
  const isCoord = Boolean(
    acaoId.value &&
    (acaoId.value === vagaItem.acaoId || !vagaItem.acaoId) &&
    props.isCoordenador
  );

  return isAutor || isAdmin || isCoord;
}

async function buscarVagas(pagina, filtros = filtrosAtivos.value) {
  carregando.value = true;

  try {
    let parametros = { page: pagina, size: 6, ...filtros };
    if (!parametros.status) {
      delete parametros.status;
    }
    if (acaoId.value) {
      parametros.acaoId = acaoId.value;
    }

    const response = await fetchVagasFiltradas(parametros);

    const dados = response?.data?.content
      ? response.data
      : response?.content
        ? response
        : response?.data;

    if (dados && (dados.content || Array.isArray(dados))) {
      const vagasBrutas = dados.content || (Array.isArray(dados) ? dados : []);
      const vagasMapeadas = await verificarCurricularizada(vagasBrutas);
      vagas.value = [...vagasMapeadas].sort((a, b) => {
        const prioridade = { ABERTA: 1, BREVE: 2, ENCERRADA: 3, CANCELADA: 4 };
        const pA = prioridade[obterStatusEfetivo(a)] || 99;
        const pB = prioridade[obterStatusEfetivo(b)] || 99;
        return pA - pB;
      });
      totalPaginas.value = dados.totalPages || totalPages.value || 1;
    } else {
      vagas.value = [];
      totalPaginas.value = 1;
    }
  } catch (error) {
    console.error("Erro ao buscar vagas:", error);
    vagas.value = [];
    totalPaginas.value = 1;
  } finally {
    carregando.value = false;
  }
}

watch(
  filtrosAtivos,
  async () => {
    const query = { ...route.query };

    filtrosAtivos.value.titulo
      ? (query.q = filtrosAtivos.value.titulo)
      : delete query.q;

    // Limpar filtros da URL para que refresh não persista estado
    delete query.dataInicial;
    delete query.dataFinal;
    delete query.isBolsistaRemunerado;
    delete query.isCurricularizada;
    delete query.isEstagiario;

    query.page = 0;

    router.push({ query: query });

    // Chamar buscarVagas diretamente com os filtros atuais
    await buscarVagas(0);
  },
  { deep: true }
);

watch(
  () => route.query,
  async () => {
    const pagina = route.query.page;
    await buscarVagas(Number(pagina) || 0);
  },
  {
    immediate: true,
  }
);

watch(acaoId, async () => {
  await buscarVagas(0);
});

onMounted(async () => {
  if (route.query.page == null) {
    togglePagina(0);
  }
});
</script>

<style scoped>
.header-menu-vagas {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 15px;
  border-bottom: 1px solid lightgray;
  width: 100%;
}

.titulo-pagina {
  padding-bottom: 0;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 16px;
}

.header-actions :deep(.barra-acoes) {
  border-bottom: none !important;
  padding: 0 !important;
  margin: 0 !important;
  width: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 16px !important;
}

#lista-vagas {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.vagas-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.paginacao-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 10px;
}

@media (max-width: 900px) {
  .vagas-grid {
    grid-template-columns: 1fr;
  }
}
/* ── Modal Usuário Não Logado ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: hsla(0, 0%, 0%, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
}

.modal-login-card {
  background: var(--branco);
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  padding: 24px;
  box-shadow:
    0 20px 25px -5px hsla(0, 0%, 0%, 0.1),
    0 10px 10px -5px hsla(0, 0%, 0%, 0.04);
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: modalFadeIn 0.25s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-login-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--roxo-superficie);
  color: var(--roxo-primario);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-login-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--cinza-escuro);
  margin: 0;
}

.modal-login-body p {
  font-size: 14px;
  color: var(--cinza-escuro);
  line-height: 1.5;
  margin: 0;
}

.modal-login-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.btn-modal-ok {
  padding: 8px 20px;
  border-radius: 8px;
  border: 1.5px solid var(--cinza-claro);
  background: var(--branco);
  color: var(--cinza-escuro);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-modal-ok:hover {
  background: var(--cinza-claro);
  border-color: var(--cinza-medio);
}

.btn-modal-entrar {
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: var(--roxo-primario);
  color: var(--branco);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px hsla(258, 53%, 28%, 0.2);
}

.btn-modal-entrar:hover {
  background: var(--roxo-primario);
  box-shadow: 0 4px 8px hsla(258, 53%, 28%, 0.3);
}
</style>
